import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Download, FileSpreadsheet, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

function downloadBlob(data, filename) {
  const url = window.URL.createObjectURL(new Blob([data]));
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  window.URL.revokeObjectURL(url);
}

export default function TripHistory({ title, subtitle, entityCard, api, entityId, fileLabel }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ date_from: '', date_to: '', search: '' });
  const [exporting, setExporting] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (filters.search) params.search = filters.search;
    api.trips(entityId, params)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load trip history'))
      .finally(() => setLoading(false));
  }, [api, entityId, filters]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const doExport = async (format) => {
    setExporting(format);
    try {
      const params = { format };
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const r = await api.exportTrips(entityId, params);
      downloadBlob(r.data, `${fileLabel}_trips.${format === 'excel' ? 'xlsx' : 'pdf'}`);
    } catch { toast.error('Export failed'); }
    finally { setExporting(''); }
  };

  const s = data?.summary || {};
  const trips = data?.trips || [];

  return (
    <div data-testid="trip-history-page" className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-100 rounded-md" data-testid="back-btn"><ArrowLeft className="w-5 h-5 text-slate-600" /></button>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900 dark:text-slate-100">{title}</h1>
          <p className="text-sm text-slate-500">{subtitle}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => doExport('pdf')} disabled={!!exporting} className="btn-secondary flex items-center gap-1.5 text-sm" data-testid="export-pdf-btn"><Download className="w-4 h-4" />{exporting === 'pdf' ? 'Exporting...' : 'PDF'}</button>
          <button onClick={() => doExport('excel')} disabled={!!exporting} className="btn-secondary flex items-center gap-1.5 text-sm" data-testid="export-excel-btn"><FileSpreadsheet className="w-4 h-4" />{exporting === 'excel' ? 'Exporting...' : 'Excel'}</button>
        </div>
      </div>

      {entityCard && data && entityCard(data)}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryCard label="Total Trips" value={s.total_trips ?? '—'} testId="summary-total-trips" />
        <SummaryCard label="This Month" value={s.month_trips ?? '—'} testId="summary-month-trips" />
        <SummaryCard label="Total Declared Value" value={s.total_declared_value != null ? `Rs. ${Number(s.total_declared_value).toLocaleString('en-IN')}` : '—'} testId="summary-declared" />
        {s.linked_revenue != null
          ? <SummaryCard label="Linked Invoice Revenue" value={`Rs. ${Number(s.linked_revenue).toLocaleString('en-IN')}`} testId="summary-revenue" />
          : <SummaryCard label="Avg Value / Trip" value={s.total_trips ? `Rs. ${Math.round((s.total_declared_value || 0) / s.total_trips).toLocaleString('en-IN')}` : '—'} testId="summary-avg" />}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div><label className="form-label">From</label><input type="date" className="form-input" value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} data-testid="filter-date-from" /></div>
        <div><label className="form-label">To</label><input type="date" className="form-input" value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} data-testid="filter-date-to" /></div>
        <div className="relative flex-1 max-w-xs">
          <label className="form-label">Search</label>
          <Search className="absolute left-3 bottom-2.5 w-4 h-4 text-slate-400" />
          <input className="form-input pl-9" placeholder="LR no / consignor / consignee" value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} data-testid="filter-search" />
        </div>
        {(filters.date_from || filters.date_to || filters.search) && <button onClick={() => setFilters({ date_from: '', date_to: '', search: '' })} className="btn-secondary text-sm mb-0.5">Clear</button>}
      </div>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md overflow-x-auto">
        <table className="w-full data-table">
          <thead><tr><th>LR Number</th><th>Date</th><th>From</th><th>To</th><th>Consignor</th><th>Consignee</th><th>Vehicle</th><th>Driver</th><th>Payment</th><th className="text-right">Declared Value</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan="10" className="text-center py-8 text-slate-400">Loading...</td></tr>
            : trips.length === 0 ? <tr><td colSpan="10" className="text-center py-8 text-slate-400">No trips found.</td></tr>
            : trips.map(t => (
              <tr key={t.receipt_id} data-testid={`trip-row-${t.receipt_id}`}>
                <td><Link to={`/receipts/${t.receipt_id}`} className="font-mono text-purple-600 hover:underline">{t.receipt_number}</Link></td>
                <td className="text-xs">{t.booking_date}</td>
                <td>{t.origin || '—'}</td>
                <td>{t.destination || '—'}</td>
                <td className="max-w-[140px] truncate">{t.consignor_name || '—'}</td>
                <td className="max-w-[140px] truncate">{t.consignee_name || '—'}</td>
                <td className="font-mono text-xs">{t.cart_vehicle_no || '—'}</td>
                <td className="text-xs">{t.driver_name || t.driver_number || '—'}</td>
                <td className="text-xs">{t.payment_type || '—'}</td>
                <td className="text-right font-mono">Rs. {(t.total_declared_value || 0).toLocaleString('en-IN')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, testId }) {
  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md p-4" data-testid={testId}>
      <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
      <p className="text-xl font-mono font-bold text-slate-900 dark:text-slate-100 mt-1">{value}</p>
    </div>
  );
}
